import * as courseActions from 'store/course/actions';
import * as sectionActions from 'store/sections/actions';
import * as contentActions from 'store/contents/actions';
import { getContents } from 'store/contents/selectors';
import eventEmitter, { events } from 'core/events/eventEmitter';
import { getSection } from 'store/sections/selectors';
import { INFORMATION_CONTENT } from 'constants/questionTypes';
import {
  isQuestionPoolEnabled,
  isScoringOfContentPagesAllowed,
  isAnswersFromPreviousAttemptEnabled,
  shouldSubmitAllQuestions
} from '../settings/selectors';
import {
  getQuestion,
  getScore,
  hasBeenOpened,
  getQuestionAttempt,
  getQuestions,
  getQuestionRetries,
  getPoolQuestions
} from './selectors';
import { getCourseAttempt } from '../course/selectors';
import * as CourseTypes from '../course/types';
import { ActionTypes } from './types';
import { ThunkResult } from '../types';

export const questionsLoaded = (questions: any): ThunkResult => (dispatch, getState) => {
  const scoreContentPages = isScoringOfContentPagesAllowed(getState());
  if (scoreContentPages && questions) {
    Object.keys(questions).forEach(key => {
      const question = questions[key];
      if (question.type === INFORMATION_CONTENT) {
        question.affectsProgress = scoreContentPages;
      }
      question.attemptNumber = question.attemptNumber ? question.attemptNumber : 0;
    });
  }
  dispatch({
    type: ActionTypes.QUESTIONS_LOADED,
    payload: questions
  });
};

export const answer = (questionId: string): ThunkResult<Promise<void>> => async (
  dispatch,
  getState
) => {
  const question = getQuestion(getState(), questionId);
  const score = question.isSurvey ? 100 : getScore(getState(), question);
  const attempt = getQuestionAttempt(getState(), questionId) + 1;
  dispatch({
    type: ActionTypes.QUESTION_ANSWERED,
    payload: { id: question.id, score, attempt }
  });

  const questionContent = getContents(getState(), question.questionContent);
  eventEmitter.emit(events.QUESTION_ANSWERED, [
    getQuestion(getState(), questionId),
    getSection(getState(), question.sectionId),
    questionContent,
    attempt
  ]);

  dispatch(sectionActions.updateProgress(question.sectionId));
  await dispatch(courseActions.updateProgress());
};

export const answerBeforeSubmit = (questionId: string): ThunkResult<Promise<void>> => async (
  dispatch,
  getState
) => {
  const question = getQuestion(getState(), questionId);
  const attempt = getCourseAttempt(getState());
  dispatch({
    type: ActionTypes.QUESTION_ANSWERED_BEFORE_SUBMIT,
    payload: { id: question.id, attempt }
  });

  const questionContent = getContents(getState(), question.questionContent);
  eventEmitter.emit(events.SUBMIT_ONCE_QUESTION_ANSWERED, [
    getQuestion(getState(), questionId),
    getSection(getState(), question.sectionId),
    questionContent,
    attempt
  ]);

  dispatch(sectionActions.updateSection(question.sectionId));
  await dispatch(courseActions.updateProgress());
};

export const updateScore = (questionId: string): ThunkResult<Promise<void>> => async (
  dispatch,
  getState
) => {
  const question = getQuestion(getState(), questionId);
  const score = question.isSurvey ? 100 : getScore(getState(), question);
  const attempt = getCourseAttempt(getState());
  dispatch({
    type: ActionTypes.QUESTION_ANSWERED,
    payload: { id: question.id, score, attempt }
  });

  const questionContent = getContents(getState(), question.questionContent);
  eventEmitter.emit(events.QUESTION_ANSWERED, [
    getQuestion(getState(), questionId),
    getSection(getState(), question.sectionId),
    questionContent,
    attempt
  ]);

  if (question.type === INFORMATION_CONTENT && isScoringOfContentPagesAllowed(getState())) {
    const section = getSection(getState(), question.sectionId);
    eventEmitter.emit(events.INFORMATION_CONTENT_EXPERIENCED, [question, section]);
  }

  dispatch(sectionActions.updateProgress(question.sectionId));
};

export const submitAllAtOnce = (): ThunkResult => (dispatch, getState) => {
  dispatch(courseActions.makeSubmitAtOnce());
  dispatch(courseActions.updateSubmitAtOnceAttemptNumber());
  const questions = isQuestionPoolEnabled(getState())
    ? getPoolQuestions(getState())
    : getQuestions(getState());
  questions.forEach(({ id }: any) => {
    dispatch(updateScore(id));
  });
  dispatch(courseActions.updateProgress());

  eventEmitter.emit(events.SUBMIT_ONCE_QUESTION_SUBMITTED, [true]);
  return true;
};

export const informationContentExperienced = (
  questionId: string
): ThunkResult<Promise<void>> => async (dispatch, getState) => {
  if (!isScoringOfContentPagesAllowed(getState())) {
    return;
  }

  dispatch({
    type: ActionTypes.QUESTION_ANSWERED,
    payload: { id: questionId, score: 100, attempt: 1 }
  });

  const question = getQuestion(getState(), questionId);

  if (shouldSubmitAllQuestions(getState())) {
    dispatch(sectionActions.updateSection(question.sectionId));
  } else {
    const section = getSection(getState(), question.sectionId);
    eventEmitter.emit(events.INFORMATION_CONTENT_EXPERIENCED, [question, section]);
    dispatch(sectionActions.updateProgress(question.sectionId));
  }

  await dispatch(courseActions.updateProgress());
};

export const markAsOpened = (questionId: string): ThunkResult<Promise<void>> => async (
  dispatch,
  getState
) => {
  if (hasBeenOpened(getState(), questionId)) {
    return;
  }

  dispatch({
    type: ActionTypes.QUESTION_OPENED,
    payload: { questionId, hasBeenOpened: true }
  });

  const questionType = getQuestion(getState(), questionId).type;
  if (questionType === INFORMATION_CONTENT) {
    dispatch(informationContentExperienced(questionId));
  }
};

export const resetProgress = (question: any): ThunkResult => (dispatch, getState) => {
  const initialScore = question.score;
  if (!isAnswersFromPreviousAttemptEnabled(getState())) {
    const isPoolQuestion = () => !!question.isPoolQuestion;
    dispatch({
      type: ActionTypes.QUESTION_PROGRESS_RESET,
      payload: { id: question.id, isPoolQuestion: isPoolQuestion(), type: question.type }
    });
    eventEmitter.emit(events.RESET_QUESTION_PROGRESS, question.id);
  } else {
    dispatch({
      type: ActionTypes.QUESTION_PROGRESS_RESET_KEEP_ANSWER,
      payload: { id: question.id }
    });
  }

  if (initialScore === 0) {
    return;
  }

  dispatch(sectionActions.updateProgress(question.sectionId));
  dispatch(courseActions.updateProgress());
};

export const cleanup = (questionId: string): ThunkResult => (dispatch, getState) => {
  const question = getQuestion(getState(), questionId);
  const isPoolQuestion = () => !!question.isPoolQuestion;
  dispatch({
    type: ActionTypes.QUESTION_PROGRESS_RESET,
    payload: { id: question.id, isPoolQuestion: isPoolQuestion(), type: question.type }
  });

  dispatch({
    type: ActionTypes.QUESTION_PROGRESS_RESET_ATTEMPT,
    payload: { id: question.id }
  });

  dispatch({
    type: ActionTypes.QUESTION_OPENED,
    payload: { questionId, hasBeenOpened: false }
  });
};

export const cleanupKeepAnswers = (questionId: string): ThunkResult => (dispatch, getState) => {
  const question = getQuestion(getState(), questionId);

  dispatch({
    type: ActionTypes.QUESTION_PROGRESS_RESET_KEEP_ANSWER,
    payload: { id: question.id }
  });

  dispatch({
    type: ActionTypes.QUESTION_OPENED,
    payload: { questionId, hasBeenOpened: false }
  });
};

export const loadFeedback = (questionId: string): ThunkResult => (dispatch, getState) => {
  const state = getState();
  const question = getQuestion(state, questionId);
  if (question.correctFeedback.length) {
    dispatch(contentActions.loadContents(getContents(state, question.correctFeedback)));
  }
  if (question.incorrectFeedback.length) {
    dispatch(contentActions.loadContents(getContents(state, question.incorrectFeedback)));
  }
  if (question.explanationBlocks.length) {
    dispatch(contentActions.loadContents(getContents(state, question.explanationBlocks)));
  }
};

export const restoreProgress = (
  questionId: string,
  questionAnswers: any,
  attempt: number,
  showPrevAnswer: boolean
): ThunkResult<Promise<void>> => async (dispatch, getState) => {
  try {
    if (!showPrevAnswer) {
      dispatch({
        type: ActionTypes.QUESTION_RESTORE_RESPONSE,
        payload: { id: questionId, response: questionAnswers, attempt }
      });
    } else {
      dispatch({
        type: ActionTypes.QUESTION_RESTORE_SHOW_PREV_ANSWER,
        payload: { id: questionId, response: questionAnswers }
      });
    }

    const question = getQuestion(getState(), questionId);
    const score = getScore(getState(), question);
    dispatch({
      type: ActionTypes.QUESTION_PROGRESS_RESTORE,
      payload: { id: questionId, score, attempt }
    });
  } catch (e) {
    console.error(e);
    dispatch({ type: CourseTypes.ActionTypes.COURSE_PROGRESS_RESTORE_FAILED, reason: e });
  }
};

export const animateFeedback = (data: {
  questionId: string;
  isAnimating: boolean;
}): ThunkResult => dispatch => {
  const { questionId, isAnimating } = data;
  dispatch({
    type: ActionTypes.FEEDBACK_ANIMATING,
    payload: { id: questionId, isFeedbackAnimating: isAnimating }
  });
};

export const updateRetries = (questionId: string): ThunkResult => (dispatch, getState) => {
  const questionRetries = getQuestionRetries(getState(), questionId);
  dispatch({
    type: ActionTypes.QUESTION_UPDATE_RETRY,
    payload: { questionId, retries: questionRetries + 1 }
  });
};
